import { useLocation, useNavigate } from "react-router-dom";
import { Column } from "../../../../core/components/Column";
import {
  CustomButton,
  SmallBackButton,
} from "../../../../core/components/CustomButton";
import { TextComponent } from "../../../../core/components/TextComponent";
import { Row } from "../../../../core/components/Row";
import lady from "../../../../core/assets/lady.png";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { useDispatch, useSelector } from "react-redux";
import { onGetVerificationCode } from "../store/SettingsThunk";
import liveFaceVerified from "../../../../core/assets/live_face_verified.png";
import liveFaceFailed from "../../../../core/assets/live_face_failed.png";

type VerificationScreenLocationState = {
  adminLiveFaceStatus?: string;
};

export enum VerificationStatus {
  Rejected = "Rejected",
  Unverified = "Unverified",
  Pending = "Pending",
  Verified = "Verified",
  LiveFaceRequired = "Live face check required",
}

export const VerificationScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const isLoading = useSelector(
    (state: RootState) => state.settings.settingsLoading,
  );
  const { getProfileConfig } = useSelector(
    (state: RootState) => state.onboarding,
  );

  const onBack = () => {
    navigate("/settings");
  };
  const onShowCodeClick = () => {
    dispatch(onGetVerificationCode()).then(() => {
      navigate("/settings/statusVerification/code");
    });
  };

  const status = getProfileConfig?.status_account;
  const isLiveFaceRequired = status === "live_face_required";
  const isVerified = status === "verified";
  const isPending = status === "pending";
  const adminLiveFaceStatus = (
    location.state as VerificationScreenLocationState | null
  )?.adminLiveFaceStatus;
  const isAdminLiveFaceFailed = adminLiveFaceStatus === "failed";
  const statusLabel = isLiveFaceRequired
    ? VerificationStatus.LiveFaceRequired
    : isVerified
      ? VerificationStatus.Verified
      : isPending
        ? VerificationStatus.Pending
    : status === "rejected"
      ? VerificationStatus.Rejected
      : VerificationStatus.Unverified;
  const adminLiveFaceMessage =
    adminLiveFaceStatus === "expired"
      ? "Live face check expired. Your account will remain suspended. You can try again."
      : adminLiveFaceStatus === "pending_result"
        ? "Live face check is still being checked. Please wait a moment and try again."
      : adminLiveFaceStatus
        ? "We could not confirm a match to your current profile photo. Your account will remain suspended. You can update your main profile photo and try again."
        : null;

  return (
    <Row className="md:bg-white p-8 max-md:overflow-y-auto">
      <Column className="flex-1">
        <SmallBackButton onClick={onBack} className="mb-4 md:mb-6" />
        <Row className="mb-6 md:mb-9 items-center flex-wrap">
          <TextComponent
            variant="h3"
            className="font-semibold mr-1 text-center md:text-left"
          >
            Account Verification:
          </TextComponent>
          <TextComponent
            variant="h3"
            className={`font-semibold text-center md:text-left ${
              isLiveFaceRequired
                ? "text-red-600"
                : isVerified
                  ? "text-green-600"
                  : "text-primary"
            }`}
          >
            {statusLabel}
          </TextComponent>
        </Row>
        <Column crossAxisAlignment="start" className="md:w-3/4 text-start">
          {isLiveFaceRequired ? (
            <>
              <TextComponent variant="h5" className="mb-4 md:mb-5 md:text-left">
                Your account is suspended until you complete a live face check.
              </TextComponent>
              <TextComponent variant="h5" className="mb-4 md:mb-5 md:text-left">
                The live face must match your current main profile photo.
              </TextComponent>
              {adminLiveFaceMessage ? (
                <TextComponent
                  variant="h5"
                  className="mb-8 md:mb-10 md:text-left"
                >
                  {adminLiveFaceMessage}
                </TextComponent>
              ) : null}
              <CustomButton
                className="md:w-2/3"
                onClick={() =>
                  navigate("/settings/statusVerification/liveness", {
                    state: { flow: "admin-live-face" },
                  })
                }
              >
                Start live face check
              </CustomButton>
            </>
          ) : isVerified ? (
            <>
              <TextComponent variant="h5" className="mb-4 md:mb-5 md:text-left">
                Your account is verified.
              </TextComponent>
              <TextComponent
                variant="h5"
                className="mb-8 md:mb-10 md:text-left"
              >
                Your live face check passed and your account has been restored.
              </TextComponent>
            </>
          ) : isPending ? (
            <TextComponent variant="h5" className="mb-4 md:mb-5 md:text-left">
              Thanks for submitting your photo! Please allow up to 24 hours for
              our staff to manually verify your profile.
            </TextComponent>
          ) : (
            <>
              <TextComponent variant="h5" className="mb-4 md:mb-5 md:text-left">
                To get verified, we will generate a unique code that you will
                need to write down on a piece of paper.
              </TextComponent>
              <TextComponent variant="h5" className="mb-4 md:mb-5 md:text-left">
                Please make sure that the numbers are written in large font and
                can easily be seen.
              </TextComponent>
              <TextComponent
                variant="h5"
                className="mb-8 md:mb-10 md:text-left"
              >
                You will then take a selfie displaying your face alongside the
                sheet of paper.
              </TextComponent>
              <CustomButton
                className="md:w-2/3"
                onClick={onShowCodeClick}
                loading={isLoading}
              >
                Show me my verification code
              </CustomButton>
            </>
          )}
        </Column>
      </Column>
      <div className="hidden md:flex flex-1 items-center justify-center mt-4 md:mt-0">
        {isVerified ? (
          <img
            src={liveFaceVerified}
            alt="Live face verified"
            className="w-full max-w-xs md:max-w-sm max-h-[26rem] object-cover rounded-xl"
          />
        ) : isAdminLiveFaceFailed ? (
          <img
            src={liveFaceFailed}
            alt="Live face check failed"
            className="w-full max-w-xs md:max-w-sm max-h-[26rem] object-cover rounded-xl"
          />
        ) : !isLiveFaceRequired ? (
          <img
            src={lady}
            alt="Lady holding a document"
            className="w-full max-w-xs md:max-w-sm max-h-[26rem] object-contain"
          />
        ) : null}
      </div>
    </Row>
  );
};
